import cx from 'classnames';
import { GovIcon, GovMessage } from '@gov-design-system-ce/react';
import {
	LocalizeNameSpaceTypes,
	useLocale,
	useTranslationWithNamespace,
} from '@gov-nx/core/service';
import { DataBoxFolderType } from '@gov-nx/module/data-box';
import { useMojeSouborySeznamContext } from '@gov-nx/module/page';
import { LayoutGap, Space } from '@gov-nx/ui/web';
import { pageRouteResolver } from '@gov-nx/web';
import { MarkdownRender } from '../../../Markdown/MarkdownRender';

export const Storage = () => {
	const { code, storage, firstDataBox } = useMojeSouborySeznamContext();
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tsn = getLocalizeCurried(code);
	const { t } = useLocale(LocalizeNameSpaceTypes.Page);

	if (storage.isLoading) {
		return null;
	}

	if (storage.error) {
		return (
			<p>{tsn('stav.chyba.behem-nacitani-stavu-uloziste-doslo-k-chybe')}</p>
		);
	}

	if (storage.data.hasEnoughFreeSpace) return null;

	return (
		<section>
			<LayoutGap gap="m">
				<p className="text-(--text-secondary)">
					<strong>{t('uloziste.uloziste-portalu-obcana')}</strong>
				</p>
				<LayoutGap gap="s">
					<div className="w-full h-4 rounded bg-(--background-block-primary)">
						<div
							className={cx(
								'h-4 rounded-l bg-(--background-status-error)',
								storage.data.isStorageFull && 'rounded-r'
							)}
							style={{
								width: `${
									storage.data.isStorageFull
										? 100
										: storage.data.usedInPercentInt
								}%`,
							}}
						/>
					</div>
					<div className="flex justify-between w-full text-body-xs text-(--text-secondary)">
						{storage.data.isStorageFull ? (
							<>
								<span title={storage.data.fullStorageInPercentFormatted}>
									<MarkdownRender>
										{t('uloziste.vyuzito-z', {
											used: storage.data?.usedFormatted ?? '',
											maximum: storage.data?.maximumFormatted ?? '',
										})}
									</MarkdownRender>
								</span>
								<span title={storage.data.maximumFormatted}>
									{t('uloziste.zaplneno')}
									<Space />
									<strong>{storage.data.fullStorageInPercentFormatted}</strong>
								</span>
							</>
						) : (
							<>
								<span title={storage.data?.usedInPercentFormatted}>
									<MarkdownRender>
										{t('uloziste.vyuzito-z', {
											used: storage.data?.usedFormatted,
											maximum: storage.data?.maximumFormatted,
										})}
									</MarkdownRender>
								</span>
								<span title={storage.data?.freeFormatted}>
									{t('uloziste.zbyva')}
									<Space />
									<strong>{storage.data?.freeInPercentFormatted}</strong>
								</span>
							</>
						)}
					</div>
				</LayoutGap>
				<GovMessage
					color="error"
					type="subtle"
					icon={
						<GovIcon
							type="components"
							name="exclamation-triangle-fill"
						/>
					}>
					<MarkdownRender>
						{t(
							'uloziste.uloziste-procistite-smazanim-nekterych-archivovanych-zprav-nebo-dokumentu',
							{
								documentUrl: pageRouteResolver('moje-soubory'),
								archiveUrl: firstDataBox?.datovaSchrankaId
									? pageRouteResolver('datove-schranky.schranka', {
											dataBoxId: firstDataBox.datovaSchrankaId,
											folderType: DataBoxFolderType.Archived,
										})
									: pageRouteResolver('datove-schranky'),
							}
						)}
					</MarkdownRender>
				</GovMessage>
			</LayoutGap>
		</section>
	);
};
